import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Talk from 'talkjs';
import { Session, Chatbox } from '@talkjs/react';
import { API_BASE_URL } from '../config/config';

export default function UserChat() {
    const location = useLocation();
    const [patientInfo, setPatientInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 쿼리 파라미터 room에서 환자 ID 파싱
    const queryParams = new URLSearchParams(location.search);
    const roomParam = queryParams.get('room') || '';
    const patientId = roomParam.replace('room_', '');

    useEffect(() => {
        // 알림 권한 요청 (웹 푸시 알림)
        if (typeof window !== 'undefined' && 'Notification' in window) {
            if (Notification.permission === 'default') {
                Notification.requestPermission().then(permission => {
                    console.log('Notification permission status:', permission);
                });
            }
        }
    }, []);

    useEffect(() => {
        const fetchPatientName = async () => {
            if (!patientId) {
                setError('유효하지 않은 대화방 주소입니다.');
                setLoading(false);
                return;
            }
            try {
                // 기존 건강 데이터 조회 API를 활용해 환자의 최신 기록 및 이름을 획득
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_BASE_URL}/api/health-data/${patientId}`, {
                    headers: token ? { Authorization: `Bearer ${token}` } : {}
                });

                if (response.data && response.data.success && response.data.data.length > 0) {
                    const latestRecord = response.data.data[0];
                    setPatientInfo({
                        id: patientId,
                        name: latestRecord.name || `사용자_${patientId}`
                    });
                } else {
                    // 데이터가 아직 조회되지 않는 경우 기본 닉네임으로 셋팅
                    setPatientInfo({
                        id: patientId,
                        name: `사용자_${patientId}`
                    });
                }
            } catch (err) {
                console.warn('환자 이름 조회 실패. 기본 식별자로 채팅을 시작합니다:', err);
                setPatientInfo({
                    id: patientId,
                    name: `사용자_${patientId}`
                });
            } finally {
                setLoading(false);
            }
        };

        fetchPatientName();
    }, [patientId]);

    const syncUser = useCallback(() => {
        if (!patientInfo) return null;
        return new Talk.User({
            id: patientInfo.id,
            name: patientInfo.name,
            email: 'patient@example.com',
            photoUrl: 'https://talkjs.com/images/avatar-2.jpg',
            welcomeMessage: '안녕하세요! 건강 상담 대화방입니다.',
            role: 'default',
        });
    }, [patientInfo]);

    const syncConversation = useCallback((session) => {
        if (!patientInfo) return null;

        const admin = new Talk.User({
            id: 'admin', // 관리자 ID 고정
            name: '관리자',
            email: 'admin@example.com',
            photoUrl: 'https://talkjs.com/images/avatar-1.jpg',
            welcomeMessage: '안녕하세요! 문의하실 내용을 입력해주세요.',
            role: 'default',
        });

        // 대화방 ID는 'room_환자ID' 형식으로 통일
        const conversationId = `room_${patientInfo.id}`;
        const conversation = session.getOrCreateConversation(conversationId);
        conversation.setParticipant(session.me);
        conversation.setParticipant(admin);
        return conversation;
    }, [patientInfo]);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '18px', color: '#666', fontFamily: 'sans-serif' }}>
                채팅방 연결 중...
            </div>
        );
    }

    if (error || !patientInfo) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '15px', color: '#ff4136', fontFamily: 'sans-serif' }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>⚠️ {error || '채팅방 정보를 불러올 수 없습니다.'}</div>
                <div style={{ fontSize: '14px', color: '#999' }}>대화방 URL을 다시 확인해주세요.</div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100vh', backgroundColor: '#f5f6f8', fontFamily: 'sans-serif' }}>
            <div style={{ padding: '16px 24px', backgroundColor: '#fff', borderBottom: '1px solid #e1e1e1', display: 'flex', alignItems: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>💬 1:1 건강 상담실</span>
            </div>
            <div style={{ flex: 1, width: '100%' }}>
                <Session 
                    appId={process.env.REACT_APP_TALKJS_APP_ID || 'tR4HG3a1'} 
                    syncUser={syncUser}
                >
                    <Chatbox
                        syncConversation={syncConversation}
                        style={{ width: '100%', height: '100%' }}
                    />
                </Session>
            </div>
        </div>
    );
}
